import { useMemo, useRef, useState } from "react"
import { CoralMap } from "@octopus-energy/coral/maps"
import { COLOUR_TOKENS } from "@octopus-energy/coral-design"
import { type Point } from "geojson"
import {
    type DataDrivenPropertyValueSpecification,
    type FilterSpecification,
    type RequestTransformFunction,
} from "mapbox-gl"
import { Layer, type MapMouseEvent, type MapRef, Source } from "react-map-gl/mapbox"
import { CIRCLE_SIZES_HIGH_ZOOM, CIRCLE_SIZES_LOW_ZOOM } from "../constants/map"
import {
    type ProducerLocation,
    type ProducerLocationFeature,
    type ProducerLocationFeatureCollection,
} from "../types"
import { ProducersMapPopup } from "./ProducersMapPopup"
import "mapbox-gl/dist/mapbox-gl.css"

const COLOR_SOLAR = COLOUR_TOKENS.background.secondary[100].base
const COLOR_WIND = COLOUR_TOKENS.background.primary[500].base
const STROKE = COLOUR_TOKENS.background.base[500].base

const SHOW_LARGE_ICONS_AT_ZOOM = 7.5
const SHOW_LARGE_ICONS_AT_ZOOM_TRANSITION = SHOW_LARGE_ICONS_AT_ZOOM - 0.25

const OEGEN_BASE_TILES_URL = process.env.NEXT_PUBLIC_OEGEN_BASE_TILES_URL
const OEGEN_TILES_URL = [`${OEGEN_BASE_TILES_URL}/{z}/{x}/{y}?layers=existing_projects`]

const SYMBOL_RADIUS_EXPRESSION = [
    "interpolate",
    ["linear"],
    ["zoom"],
    SHOW_LARGE_ICONS_AT_ZOOM_TRANSITION,
    ["step", ["get", "avg_no_of_homes_powered"], ...CIRCLE_SIZES_LOW_ZOOM],
    SHOW_LARGE_ICONS_AT_ZOOM,
    ["step", ["get", "avg_no_of_homes_powered"], ...CIRCLE_SIZES_HIGH_ZOOM],
] as DataDrivenPropertyValueSpecification<number>

const SYMBOL_COLOR_EXPRESSION = [
    "case",
    ["==", ["get", "technology"], "SOLAR"],
    COLOR_SOLAR,
    COLOR_WIND,
] as DataDrivenPropertyValueSpecification<string>

const ICON_LAYER_LAYOUT = {
    "icon-image": [
        "concat",
        ["downcase", ["get", "technology"]],
        "-icon",
    ] as DataDrivenPropertyValueSpecification<string>,
    "icon-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        SHOW_LARGE_ICONS_AT_ZOOM_TRANSITION,
        0,
        SHOW_LARGE_ICONS_AT_ZOOM,
        1,
    ] as DataDrivenPropertyValueSpecification<number>,
    // "icon-allow-overlap": true,
}

const OEGEN_FILTER = [
    "all",
    ["==", ["get", "country"], "France"],
    // ignore anything other than "wind" and "solar" (ie "biomass", etc)
    ["any", ["==", ["get", "technology"], "WIND"], ["==", ["get", "technology"], "SOLAR"]],
] as FilterSpecification

export function ProducersMap({ locations }: { locations: ProducerLocation[] }) {
    const geoJSON: ProducerLocationFeatureCollection = useMemo(
        () => ({
            type: "FeatureCollection",
            features: locations
                .filter((location) => {
                    if (location.end_date) {
                        const now = new Date()
                        const endDate = new Date(location.end_date)

                        if (location.start_date) {
                            const startDate = new Date(location.start_date)

                            return +startDate <= +now && +endDate >= +now
                        }

                        return +endDate >= +now
                    }

                    return true
                })
                .map((location, id) => ({
                    id,
                    type: "Feature",
                    properties: {
                        ...location,
                    },
                    geometry: {
                        type: "Point",
                        coordinates: [
                            location.coordinates.longitude,
                            location.coordinates.latitude,
                        ],
                    },
                })),
        }),
        [locations]
    )

    const onMapClick = (event: MapMouseEvent) => {
        const clickedFeature = event.features?.[0] as ProducerLocationFeature | undefined
        if (!clickedFeature) return

        const [lng, lat] = (clickedFeature.geometry as Point).coordinates

        mapRef.current?.flyTo({
            center: [lng, lat],
            zoom: 16,
        })

        setPopupData(clickedFeature)
    }

    const onMouseEnter = () => setCursor("pointer")
    const onMouseLeave = () => setCursor("auto")

    const [popupData, setPopupData] = useState<ProducerLocationFeature>()
    const [cursor, setCursor] = useState<string>("auto")
    const mapRef = useRef<MapRef>(null)

    const transformRequest = (
        url: string,
        resourceType:
            | "Tile"
            | "Unknown"
            | "Style"
            | "Source"
            | "Glyphs"
            | "SpriteImage"
            | "SpriteJSON"
            | "Iconset"
            | "Image"
            | "Model"
            | undefined
    ) => {
        // Target only tile requests going to the specific Octopus API endpoint
        if (resourceType === "Tile" && url.startsWith(OEGEN_BASE_TILES_URL || "")) {
            return {
                url,
                headers: {
                    Authorization: process.env.NEXT_PUBLIC_OEGEN_TILES_KEY,
                },
            }
        }

        // Return null or undefined to let react-map-gl handle default behavior for other requests
        return { url } // Explicitly return url is also safe
    }

    return (
        <CoralMap
            mapboxAccessToken={process.env.NEXT_PUBLIC_MAPBOX_TOKEN}
            ref={mapRef}
            initialViewStatePreset="FRA"
            transformRequest={transformRequest as RequestTransformFunction}
            onClick={onMapClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            interactiveLayerIds={["partner-circles", "oegen-circles"]}
            cursor={cursor}
        >
            <Source
                data={geoJSON}
                type="geojson"
                id="partners"
                key="partners"
            >
                <Layer
                    id="partner-circles"
                    type="circle"
                    source="partners"
                    paint={{
                        "circle-radius": SYMBOL_RADIUS_EXPRESSION,
                        "circle-color": SYMBOL_COLOR_EXPRESSION,
                        "circle-stroke-color": STROKE, // colors.background.base.500
                        "circle-stroke-width": 0.5,
                    }}
                    layout={{
                        "circle-sort-key": ["*", -1, ["get", "avg_no_of_homes_powered"]],
                    }}
                />
                <Layer
                    id="partner-icons"
                    type="symbol"
                    source="partners"
                    layout={ICON_LAYER_LAYOUT}
                />
            </Source>

            <Source
                type="vector"
                id="oegen"
                key="oegen"
                tiles={OEGEN_TILES_URL}
            >
                <Layer
                    id="oegen-circles"
                    type="circle"
                    source-layer="existing_projects"
                    source="oegen"
                    paint={{
                        "circle-radius": SYMBOL_RADIUS_EXPRESSION,
                        "circle-color": SYMBOL_COLOR_EXPRESSION,
                        "circle-stroke-color": STROKE, // colors.background.base.500
                        "circle-stroke-width": 2,
                    }}
                    layout={{
                        "circle-sort-key": ["*", -1, ["get", "avg_no_of_homes_powered"]],
                    }}
                    filter={OEGEN_FILTER}
                />
                <Layer
                    id="oegen-icons"
                    type="symbol"
                    source-layer="existing_projects"
                    source="oegen"
                    layout={ICON_LAYER_LAYOUT}
                    filter={OEGEN_FILTER}
                />
            </Source>
            {popupData && (
                <ProducersMapPopup
                    data={popupData}
                    setData={setPopupData}
                />
            )}
        </CoralMap>
    )
}

ProducersMap.messages = ["ProducersMapPage", ...ProducersMapPopup.messages] as const
